import {
    Checkbox,
    DefaultButton,
    Dropdown,
    IDropdownOption,
    MessageBar,
    MessageBarType,
    PrimaryButton,
    ProgressIndicator,
    Stack,
    Text,
    TextField,
} from "@fluentui/react";
import {useBoolean} from "@fluentui/react-hooks";
import React, {useCallback, useEffect, useMemo, useState} from "react";
import {useTranslation} from "react-i18next";
import {externalAuthErrorMessage} from "../helpers/ExternalAuthWebHelper";
import {
    createMcpApiKey,
    loadMcpApiKeys,
    McpApiKey,
    mcpApiKeyEndpointPath,
    McpApiKeyEntitlements,
    mcpApiKeyHeader,
    McpApiKeyKind,
    mcpApiKeyKindTitle,
    mcpApiKeyPermissionTitles,
    revokeMcpApiKey,
} from "../helpers/McpApiKeysHelper";
import {TitleAndContentLayout} from "./TitleAndContentLayout";

type Message = {
    type: MessageBarType;
    text: string;
};

const expiryOptions: IDropdownOption[] = [
    {key: "30", text: "30 dana"},
    {key: "90", text: "90 dana"},
    {key: "365", text: "1 godina"},
    {key: "never", text: "Bez isteka"},
];

const formatDate = (value?: string | null): string =>
    value ? new Date(value).toLocaleString() : "-";

const keyStatus = (key: McpApiKey): string => {
    if (key.revokedAt) return `Opozvan ${formatDate(key.revokedAt)}`;
    if (!key.isActive) return `Istekao ${formatDate(key.expiresAt)}`;
    return key.expiresAt ? `Vrijedi do ${formatDate(key.expiresAt)}` : "Bez isteka";
};

export const UserSettingsMcpApiKeys = () => {
    const {t} = useTranslation();
    const [keys, setKeys] = useState<McpApiKey[]>([]);
    const [entitlements, setEntitlements] = useState<McpApiKeyEntitlements>();
    const [message, setMessage] = useState<Message>();
    const [loading, {setTrue: setLoading, setFalse: setNotLoading}] = useBoolean(false);

    const [kind, setKind] = useState<McpApiKeyKind>("SUPPORT");
    const [name, setName] = useState("");
    const [expiry, setExpiry] = useState("365");
    const [permissions, setPermissions] = useState<string[]>([]);
    const [createdKey, setCreatedKey] = useState<{key: string; apiKey: McpApiKey}>();

    const kindOptions = useMemo<IDropdownOption[]>(() => {
        const options: IDropdownOption[] = [];
        if (entitlements?.canCreateSupportKey) {
            options.push({key: "SUPPORT", text: mcpApiKeyKindTitle("SUPPORT")});
        }
        if (entitlements?.canCreateOnixServerKey) {
            options.push({key: "ONIX_SERVER", text: mcpApiKeyKindTitle("ONIX_SERVER")});
        }
        return options;
    }, [entitlements]);

    const allowedPermissions = useMemo(
        () =>
            kind === "SUPPORT"
                ? entitlements?.supportPermissions ?? []
                : entitlements?.onixServerPermissions ?? [],
        [entitlements, kind],
    );

    const refresh = useCallback(async () => {
        setLoading();
        try {
            const result = await loadMcpApiKeys();
            setKeys(result.keys);
            setEntitlements(result.entitlements);
            if (!result.entitlements.canCreateSupportKey && result.entitlements.canCreateOnixServerKey) {
                setKind("ONIX_SERVER");
            }
        } catch (error) {
            setMessage({
                type: MessageBarType.error,
                text: externalAuthErrorMessage(error, "Nije moguće učitati MCP ključeve."),
            });
        } finally {
            setNotLoading();
        }
    }, [setLoading, setNotLoading]);

    useEffect(() => {
        refresh();
    }, [refresh]);

    const togglePermission = (permission: string, checked?: boolean) =>
        setPermissions((current) =>
            checked
                ? [...current.filter((p) => p !== permission), permission]
                : current.filter((p) => p !== permission),
        );

    const create = async () => {
        setLoading();
        setMessage(undefined);
        setCreatedKey(undefined);
        try {
            const result = await createMcpApiKey({
                kind,
                name: name.trim(),
                expiresInDays: expiry === "never" ? null : Number(expiry),
                permissions: permissions.filter((p) => allowedPermissions.includes(p)),
            });
            setCreatedKey(result);
            setName("");
            setPermissions([]);
            const loaded = await loadMcpApiKeys();
            setKeys(loaded.keys);
        } catch (error) {
            setMessage({
                type: MessageBarType.error,
                text: externalAuthErrorMessage(error, "Ključ nije moguće kreirati."),
            });
        } finally {
            setNotLoading();
        }
    };

    const revoke = async (key: McpApiKey) => {
        if (!window.confirm(`Opozvati ključ "${key.name}"? Alati koji ga koriste odmah gube pristup.`)) return;

        setLoading();
        setMessage(undefined);
        try {
            await revokeMcpApiKey(key.id);
            setMessage({type: MessageBarType.success, text: "Ključ je opozvan."});
            const loaded = await loadMcpApiKeys();
            setKeys(loaded.keys);
        } catch (error) {
            setMessage({
                type: MessageBarType.error,
                text: externalAuthErrorMessage(error, "Ključ nije moguće opozvati."),
            });
        } finally {
            setNotLoading();
        }
    };

    const copyCreatedKey = async () => {
        if (!createdKey) return;
        try {
            await navigator.clipboard.writeText(createdKey.key);
            setMessage({type: MessageBarType.success, text: "Ključ je kopiran."});
        } catch {
            setMessage({type: MessageBarType.warning, text: "Kopiranje nije uspjelo. Označite ključ i kopirajte ga ručno."});
        }
    };

    const canCreate = kindOptions.length > 0;

    return (
        <TitleAndContentLayout title={t("mcp-api-keys")} lessPadding isSubtitle pad>
            <Stack tokens={{childrenGap: 16}} styles={{root: {maxWidth: 640}}}>
                <Text>
                    Osobni ključevi za MCP alate (Claude, Codex, IBTech MCP Hub). Ključ radi u vaše ime i vidi samo
                    ono što vi smijete vidjeti. Prava se provjeravaju pri svakom pozivu, pa promjena vaše uloge ili
                    paketa tvrtke odmah vrijedi i za ključ.
                </Text>

                {message ? <MessageBar messageBarType={message.type}>{message.text}</MessageBar> : null}

                {createdKey ? (
                    <MessageBar messageBarType={MessageBarType.warning} isMultiline>
                        <Stack tokens={{childrenGap: 8}}>
                            <Text>
                                Ključ <b>{createdKey.apiKey.name}</b> je kreiran. Kopirajte ga sada: prikazuje se samo
                                jednom i ne može se ponovno dohvatiti.
                            </Text>
                            <TextField readOnly value={createdKey.key} onFocus={(e) => e.target.select()}/>
                            <Text variant="small">
                                Zaglavlje: {mcpApiKeyHeader(createdKey.apiKey.kind)} (ili Authorization: Bearer).
                                Adresa: {window.location.origin}
                                {mcpApiKeyEndpointPath(createdKey.apiKey.kind)}
                            </Text>
                            <Stack horizontal tokens={{childrenGap: 8}}>
                                <PrimaryButton text="Kopiraj ključ" onClick={copyCreatedKey}/>
                                <DefaultButton text="Gotovo" onClick={() => setCreatedKey(undefined)}/>
                            </Stack>
                        </Stack>
                    </MessageBar>
                ) : null}

                <Stack tokens={{childrenGap: 8}}>
                    <Text variant="large">Novi ključ</Text>
                    {!entitlements ? null : !canCreate ? (
                        <Text>Vaš korisnik nema pravo na MCP ključeve. Obratite se administratoru.</Text>
                    ) : (
                        <>
                            <Dropdown
                                label="Vrsta"
                                options={kindOptions}
                                selectedKey={kind}
                                onChange={(_, option) => {
                                    setKind(option?.key as McpApiKeyKind);
                                    setPermissions([]);
                                }}
                            />
                            <TextField
                                label="Naziv"
                                placeholder="npr. Claude na laptopu"
                                value={name}
                                maxLength={100}
                                onChange={(_, value) => setName(value ?? "")}
                            />
                            <Dropdown
                                label="Trajanje"
                                options={expiryOptions}
                                selectedKey={expiry}
                                onChange={(_, option) => setExpiry(String(option?.key ?? "365"))}
                            />
                            <Text variant="small">
                                Čitanje je uvijek uključeno. Dodatna dopuštenja:
                            </Text>
                            {allowedPermissions.length === 0 ? (
                                <Text variant="small">Nema dodatnih dopuštenja koja možete dodijeliti.</Text>
                            ) : (
                                allowedPermissions.map((permission) => (
                                    <Checkbox
                                        key={permission}
                                        label={mcpApiKeyPermissionTitles[permission] ?? permission}
                                        checked={permissions.includes(permission)}
                                        onChange={(_, checked) => togglePermission(permission, checked)}
                                    />
                                ))
                            )}
                            <Stack horizontal>
                                <PrimaryButton
                                    text="Kreiraj ključ"
                                    disabled={loading || name.trim().length === 0}
                                    onClick={create}
                                />
                            </Stack>
                        </>
                    )}
                </Stack>

                <Stack tokens={{childrenGap: 8}}>
                    <Text variant="large">Moji ključevi</Text>
                    {keys.length === 0 ? (
                        <Text>Nemate MCP ključeva.</Text>
                    ) : (
                        keys.map((key) => (
                            <Stack
                                horizontal
                                horizontalAlign="space-between"
                                verticalAlign="center"
                                tokens={{childrenGap: 16}}
                                key={key.id}
                                styles={{
                                    root: {
                                        borderBottom: "1px solid #edebe9",
                                        paddingBottom: 8,
                                        paddingTop: 8,
                                        opacity: key.isActive ? 1 : 0.6,
                                    },
                                }}
                            >
                                <Stack tokens={{childrenGap: 2}}>
                                    <Text>
                                        {key.name} ({mcpApiKeyKindTitle(key.kind)})
                                    </Text>
                                    <Text variant="small">
                                        {key.keyPrefix}... · {keyStatus(key)} · zadnje korišten{" "}
                                        {formatDate(key.lastUsedAt)}
                                    </Text>
                                    {key.permissions.length > 0 ? (
                                        <Text variant="small">
                                            {key.permissions
                                                .map((p) => mcpApiKeyPermissionTitles[p] ?? p)
                                                .join(", ")}
                                        </Text>
                                    ) : null}
                                </Stack>
                                {key.isActive ? (
                                    <DefaultButton
                                        type="button"
                                        text="Opozovi"
                                        disabled={loading}
                                        onClick={() => revoke(key)}
                                    />
                                ) : null}
                            </Stack>
                        ))
                    )}
                </Stack>

                {loading ? <ProgressIndicator/> : null}
            </Stack>
        </TitleAndContentLayout>
    );
};
